import { useEffect, useState } from "react";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { navLinks, WA_DEFAULT } from "../../lib/site";
import { scrollToId } from "../../lib/scroll";
import { Magnetic } from "./Magnetic";

export const PixelMark = ({ size = 26 }) => (
  <img
    src="/assets/logo-peak.png"
    style={{ height: size, width: "auto" }}
    className="object-contain drop-shadow-[0_0_10px_rgba(204,255,2,0.35)]"
    alt="STUZIO. EDITFLOW logo mark"
    data-testid="brand-pixel-mark"
  />
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(href), 350);
    } else scrollToId(href);
  };

  return (
    <>
      <header data-testid="site-nav" className="fixed top-3 sm:top-5 inset-x-0 z-50 px-3 sm:px-5">
        <div
          className={`max-w-6xl mx-auto h-14 sm:h-16 pl-4 sm:pl-5 pr-2 flex items-center justify-between rounded-full border transition-all duration-500 ${
            scrolled
              ? "bg-ink/85 backdrop-blur-xl border-white/10 shadow-[0_12px_44px_rgba(0,0,0,0.55)]"
              : "bg-ink/40 backdrop-blur-md border-white/[0.07]"
          }`}
        >
          <Link to="/" data-testid="nav-logo" className="group relative flex items-center gap-3" aria-label="STUZIO. EDITFLOW home">
            <span className="absolute -inset-3 rounded-2xl bg-brand/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />
            <img
              src="/assets/logo-peak.png"
              alt="STUZIO. EDITFLOW pixel diamond logo mark"
              className="relative h-8 w-auto object-contain drop-shadow-[0_0_12px_rgba(204,255,2,0.4)] group-hover:scale-105 group-hover:drop-shadow-[0_0_20px_rgba(204,255,2,0.6)] transition-all duration-500"
            />
            <span className="relative leading-none">
              <span className="block font-sans font-extrabold tracking-[0.14em] text-[15px] text-white">
                STUZIO<span className="text-brand">.</span>
              </span>
              <span className="block font-mono text-[9px] tracking-[0.46em] text-mute group-hover:text-white transition-colors duration-300 mt-1.5">
                EDITFLOW
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {navLinks.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className="nav-link text-[13px] font-medium tracking-wide text-mute hover:text-white transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Magnetic>
              <a
                data-testid="nav-lets-talk-button"
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-3 pl-5 pr-1.5 py-1.5 rounded-full border border-brand/60 text-white text-[13px] font-semibold tracking-wide hover:bg-brand/10 transition-colors duration-300"
              >
                Let&apos;s Talk
                <span className="btn-arrow-box"><ArrowUpRight size={15} /></span>
              </a>
            </Magnetic>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(true)}
              className="lg:hidden text-white p-2"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-ink/97 backdrop-blur-2xl flex flex-col"
          >
            <div className="h-[72px] px-5 flex items-center justify-between border-b hairline">
              <span className="flex items-center gap-2.5">
                <img src="/assets/logo-peak.png" alt="STUZIO. EDITFLOW pixel diamond logo mark" className="h-8 w-auto object-contain drop-shadow-[0_0_10px_rgba(204,255,2,0.4)]" />
                <span className="leading-none">
                  <span className="block font-sans font-extrabold tracking-[0.14em] text-sm text-white">STUZIO<span className="text-brand">.</span></span>
                  <span className="block font-mono text-[8px] tracking-[0.46em] text-mute mt-1">EDITFLOW</span>
                </span>
              </span>
              <button data-testid="nav-mobile-close-button" onClick={() => setOpen(false)} className="text-white p-2" aria-label="Close menu"><X size={24} /></button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1" aria-label="Mobile">
              {navLinks.map((l, i) => (
                <motion.button
                  key={l.label}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-light text-4xl text-white py-3 border-b hairline hover:text-brand transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.a
                data-testid="nav-mobile-lets-talk"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-lime mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 font-semibold text-sm tracking-wide"
              >
                Let&apos;s Talk on WhatsApp <ArrowUpRight size={16} />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
